import { ref, reactive, watch, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetGroup,
  localStorageGetUserId,
  localStorageGetShopCode,
  localStorageGetAuthType,
} from "@/common/localStorageUtil";
import {
  GetChainDocument,
  type GetChainQuery,
  type TransPickupRequestListInput,
  type TransPickupRequestCountInput,
  ListTransPickupRequestDocument,
  type ListTransPickupRequestQuery,
  type ListTransPickupRequestQueryVariables,
  type OperationInput,
  type ShopListInput,
  type ShopListForTransPickupRequestInput,
  type TransPickupRequestUpdateInput,
} from "@/generated/graphql";
import type { ApolloQueryResult } from "@apollo/client/core";
import { displayPagingRange as displayPagingRangeFunction } from "@/common/common";
import { checkChainListDisplay } from "@/common/business";
import { isOperatorGroupShop as isOperatorGroupShopFunction } from "@/common/business";
import { DeliveryRequest } from "@/common/constant";
import { getWordList } from "@/common/graphql/expression";
import { getChainPaymentMethod } from "@/common/graphql/chain";
import { getChainList } from "@/common/graphql/chain";
import { getShopList } from "@/common/graphql/shop";
import type { ListData, ListOptions, ChainPaymentMethodItem } from "../types";

/**
 * 画面で共有する状態を初期化して返却する composable。
 *
 * @returns 画面で利用するリアクティブな状態オブジェクト
 */
export const useDeliveryMoveInitState = () => {
  // 配達タイプ
  const deliveryTypeItems = ref(DeliveryRequest.move_type);

  //所属グループの状態
  const isOperatorGroupShop = ref(isOperatorGroupShopFunction(localStorageGetGroup()));

  // 店舗名をdisableにするかのフラグ
  const shopDisabledFlag = ref(true);

  // 店舗一覧 input
  const shopListInput = reactive<ShopListInput>({
    chain_code: localStorageGetChainCode(),
    customer_code: localStorageGetCustomerCode(),
    limit: 10000,
    offset: 0,
  });

  /**
   * 配達先店舗一覧 input
   */
  const shopToListInput = ref<ShopListForTransPickupRequestInput>({
    chain_code: localStorageGetChainCode(),
    customer_code: localStorageGetCustomerCode(),
    to_shop_code: "",
    from_shop_code: "",
  });

  /**
   * 配達元店舗一覧 input
   */
  const shopFromListInput = ref<ShopListForTransPickupRequestInput>({
    chain_code: localStorageGetChainCode(),
    customer_code: localStorageGetCustomerCode(),
    to_shop_code: "",
    from_shop_code: "",
  });

  // 店舗
  const shopListItems = ref<(
    { __typename?: "ShopResponse"; shop_code: string; name: string } | null
  )[]>([]);

  // 店舗(配達先)
  const shopToListItems = ref<(
    { __typename?: "ShopResponse"; shop_code: string; name: string } | null
  )[]>([]);

  // 店舗(配達元)
  const shopFromListItems = ref<(
    { __typename?: "ShopResponse"; shop_code: string; name: string } | null
  )[]>([]);

  const bufShopListItems = ref<(
    { __typename?: "ShopResponse"; shop_code: string; name: string } | null
  )[]>([]);

  const shopFromAllCheckFlag = ref<string>("none");
  const shopToAllCheckFlag = ref<string>("none");

  const shopSelectValues_from = ref<{ shop_code: string; name: string }>({ shop_code: "", name: "" });
  const shopSelectValues_to = ref<{ shop_code: string; name: string }>({ shop_code: "", name: "" });

  const searchConditionChainCode = ref<string[]>([]);
  const searchShopSelectValuesFrom = ref<string[]>([]);
  const searchShopSelectValuesTo = ref<string[]>([]);

  const shopSelectValuesFrom = ref<{ shop_code: string; name: string }[]>([]);
  const shopSelectValuesTo = ref<{ shop_code: string; name: string }[]>([]);

  // 店舗情報
  const shopData = ref<{
    __typename?: "ShopResponse";
    phone?: string | null;
    name: string;
    address1: string;
    address2: string;
    address3?: string | null;
    holiday_fri: number;
    holiday_mon: number;
    holiday_sat: number;
    holiday_sun: number;
    holiday_thu: number;
    holiday_tue: number;
    holiday_wed: number;
    chain_code: string;
    created_date: string;
    created_user_id: string;
    customer_code: string;
    fax?: string | null;
    shop_code: string;
    updated_date?: string | null;
    updated_user_id?: string | null;
    zip_code: string;
    double_deli_oricon_count?: number | null;
    charter_deli_oricon_count?: number | null;
  } | null>(null);

  const shopData_from_address = ref<string>("");
  const shopData_from_address1 = ref<string>("");
  const shopData_to_address = ref<string>("");
  const shopData_to_address1 = ref<string>("");

  // 権限
  type AuthType = string;
  const authType = ref<AuthType>("");

  // マウント済フラグ
  const isMounted = ref(false);

  // ユーザ権限取得
  const chainViewFlag = ref(checkChainListDisplay(localStorageGetAuthType()));

  // チェーンリスト
  const chainListItems = ref<
    (
      | { __typename?: "Chain"; chain_code: string; name: string }
      | null
      | undefined
    )[] | null | undefined
  >([]);

  const bufChainListItems = ref<
    (
      | { __typename?: "Chain"; chain_code: string; name: string }
      | null
      | undefined
    )[] | null | undefined
  >([]);
  const chainSelectValues = ref<{ chain_code: string; name: string }[]>([]);
  const chainAllCheckFlag = ref<string>("all");
  const areaSelectValues = ref<{ area_code: string; name: string }>({
    area_code: "",
    name: "",
  });
  const unitItems = ref<number[]>([]);
  const paymentRequiredFlg = ref(false);
  const paymentList = ref<
    (ChainPaymentMethodItem | null | undefined)[] | null | undefined
  >([]);

  // 編集対象 配達依頼
  const chainPaymentMethodSelectValues = ref<{ item: string; index: string } | null>({ item: "", index: "" });
  const editDeliveryRequest = ref<TransPickupRequestUpdateInput | null>(null);
  const paymentMethodItems = ref<(string | undefined)[]>([]);

  const dummy_payment_amount = ref<string>("");

  // 置換前の備考蘭
  const deliveryNotesBefore = ref("");
  const messageDeliveryNotes = ref("");

  // isDisabled
  const isDisabled = ref(false);
  const isDisabledChangeFromTo = ref(false);

  const wordList = reactive<Record<string, string>>({});

  // ページング範囲の表示
  const displayPagingRange = ref<string>("");

  return {
    deliveryTypeItems,
    isOperatorGroupShop,
    shopDisabledFlag,
    shopListInput,
    shopToListInput,
    shopFromListInput,
    shopListItems,
    shopToListItems,
    shopFromListItems,
    bufShopListItems,
    shopFromAllCheckFlag,
    shopToAllCheckFlag,
    shopSelectValues_from,
    shopSelectValues_to,
    searchConditionChainCode,
    searchShopSelectValuesFrom,
    searchShopSelectValuesTo,
    shopSelectValuesFrom,
    shopSelectValuesTo,
    shopData,
    shopData_from_address,
    shopData_from_address1,
    shopData_to_address,
    shopData_to_address1,
    authType,
    isMounted,
    chainViewFlag,
    chainListItems,
    bufChainListItems,
    chainSelectValues,
    chainAllCheckFlag,
    areaSelectValues,
    unitItems,
    paymentRequiredFlg,
    paymentList,
    chainPaymentMethodSelectValues,
    editDeliveryRequest,
    paymentMethodItems,
    dummy_payment_amount,
    deliveryNotesBefore,
    messageDeliveryNotes,
    isDisabled,
    isDisabledChangeFromTo,
    wordList,
    displayPagingRange,
  };
};

interface UseDeliveryMoveInitParams {
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  overlay: Ref<boolean>;
  displayListSearchDialog: Ref<boolean>;
  authType: Ref<string>;
  isMounted: Ref<boolean>;
  chainViewFlag: Ref<boolean>;
  chainListItems: Ref<any>;
  bufChainListItems: Ref<any>;
  chainSelectValues: Ref<{ chain_code: string; name: string }[]>;
  chainAllCheckFlag: Ref<string>;
  areaSelectValues: Ref<{ area_code: string; name: string }>;
  unitItems: Ref<number[]>;
  paymentRequiredFlg: Ref<boolean>;
  paymentList: Ref<any>;
  paymentMethodItems: Ref<(string | undefined)[]>;
  editDeliveryRequest: Ref<TransPickupRequestUpdateInput | null>;
  shopListInput: ShopListInput;
  shopListItems: Ref<any>;
  shopFromListItems: Ref<any>;
  shopToListItems: Ref<any>;
  bufShopListItems: Ref<any>;
  shopFromAllCheckFlag: Ref<string>;
  shopToAllCheckFlag: Ref<string>;
  shopDisabledFlag: Ref<boolean>;
  isOperatorGroupShop: Ref<boolean>;
  shopSelectValuesFrom: Ref<{ shop_code: string; name: string }[]>;
  shopSelectValuesTo: Ref<{ shop_code: string; name: string }[]>;
  searchConditionChainCode: Ref<string[]>;
  searchShopSelectValuesFrom: Ref<string[]>;
  searchShopSelectValuesTo: Ref<string[]>;
  searchConditionReceptionNo: Ref<string>;
  searchConditionReceptionDateFrom: Ref<Date | null>;
  searchConditionReceptionDateTo: Ref<Date | null>;
  searchConditionDeliveryDateFrom: Ref<Date | null>;
  searchConditionDeliveryDateTo: Ref<Date | null>;
  deliveryTypeValues: Ref<{ label: string; value: number }>;
  statusSelectedItem: Ref<string[]>;
  listOptions: Ref<ListOptions>;
  listDataArray: Ref<ListData[]>;
  listDataTotalCount: Ref<number>;
  listLoading: Ref<boolean>;
  selectListCheckbox: Ref<ListData[]>;
  displayPagingRange: Ref<string>;
  wordList: Record<string, string>;
  formatDateForDisplay: (date: Date | null) => string | null;
  pickupDeliveryStatusName: (id: string | null | undefined) => string;
  pickupDeliveryTypeName: (value: number | null | undefined) => string;
  initTableHeaders: () => void;
}

/**
 * 画面表示時に必要な初期化処理を提供する composable。
 *
 * @returns 初期化処理を実行する関数群と初期表示に必要な状態
 */
export const useDeliveryMoveInit = ({
  errdialog,
  errMessage,
  overlay,
  displayListSearchDialog,
  authType,
  isMounted,
  chainViewFlag,
  chainListItems,
  bufChainListItems,
  chainSelectValues,
  chainAllCheckFlag,
  areaSelectValues,
  unitItems,
  paymentRequiredFlg,
  paymentList,
  paymentMethodItems,
  editDeliveryRequest,
  shopListInput,
  shopListItems,
  shopFromListItems,
  shopToListItems,
  bufShopListItems,
  shopFromAllCheckFlag,
  shopToAllCheckFlag,
  shopDisabledFlag,
  isOperatorGroupShop,
  shopSelectValuesFrom,
  shopSelectValuesTo,
  searchConditionChainCode,
  searchShopSelectValuesFrom,
  searchShopSelectValuesTo,
  searchConditionReceptionNo,
  searchConditionReceptionDateFrom,
  searchConditionReceptionDateTo,
  searchConditionDeliveryDateFrom,
  searchConditionDeliveryDateTo,
  deliveryTypeValues,
  statusSelectedItem,
  listOptions,
  listDataArray,
  listDataTotalCount,
  listLoading,
  selectListCheckbox,
  displayPagingRange,
  wordList,
  formatDateForDisplay,
  pickupDeliveryStatusName,
  pickupDeliveryTypeName,
  initTableHeaders,
}: UseDeliveryMoveInitParams) => {
  // 検索ボタンからの呼び出し時にwatchをスキップするフラグ
  const isSearchButtonClick = ref(false);

  /**
   * 絞り込み検索 @Click
   */
  const onClickSearch = async (initail_mode = false): Promise<void> => {
    // 検索ボタンをクリックした場合、ページを1に戻す（既に1ページ目の場合はスキップ）
    if (!initail_mode && listOptions.value.page !== 1) {
      isSearchButtonClick.value = true;
      listOptions.value.page = 1;
    }

    // 店舗ユーザの場合、配達元か配達先がログインユーザの店舗でない場合エラー
    // 本部ユーザの場合、配達元が指定されていない場合、処理終了
    if (isOperatorGroupShop.value) {
      let checkFrom = false;
      let checkOtherFrom = false;
      let checkSelfFrom = false;

      let checkTo = false;
      let checkOtherTo = false;
      let checkSelfTo = false;

      if (!isMounted.value) {
        // 店舗情報を取得する
        bufShopListItems.value = await getShopList(shopListInput);

        // 店舗権限の場合は自身の店舗を配送元のデフォルトにする
        if (bufShopListItems.value.length > 0) {
          bufShopListItems.value.forEach((shop_value: any) => {
            if (shop_value!.shop_code === localStorageGetShopCode()) {
              searchShopSelectValuesFrom.value.push(shop_value!.shop_code);
            }
            searchShopSelectValuesTo.value.push(shop_value!.shop_code);
          });
        }
      }

      // 配送元
      if (searchShopSelectValuesFrom.value.length >= 1) {
        searchShopSelectValuesFrom.value.forEach((shop_code) => {
          if (shop_code === localStorageGetShopCode()) {
            // 自身の店舗がチェックされている
            checkSelfFrom = true;
          } else {
            // 他店の店舗がチェックされている
            checkOtherFrom = true;
          }
        });

        // 自身の店舗がチェックされていないまたは他店の店舗がチェックされている
        checkFrom = checkSelfFrom && !checkOtherFrom;
      } else {
        checkFrom = false;
      }

      // 配送先
      if (searchShopSelectValuesTo.value.length >= 1) {
        searchShopSelectValuesTo.value.forEach((shop_code) => {
          if (shop_code === localStorageGetShopCode()) {
            // 自身の店舗がチェックされている
            checkSelfTo = true;
          } else {
            // 他店の店舗がチェックされている
            checkOtherTo = true;
          }
        });

        // 自身の店舗がチェックされていないまたは他店の店舗がチェックされている
        checkTo = checkSelfTo && !checkOtherTo;
      } else {
        checkTo = false;
      }

      if (!checkFrom && !checkTo) {
        // 配達元と配達先両方とも空の場合、検索しない
        if (!initail_mode) {
          errdialog.value = true;
          errMessage.value =
            "配達元または配達先はログインユーザの店舗のみを指定してください";
          return;
        }
      }
    }

    // graphql接続
    const apolloClient = await generateApolloClient();

    // 在庫移動・回収依頼一覧 input
    const listInput: TransPickupRequestListInput = {
      reception_no:
        searchConditionReceptionNo.value !== undefined &&
        searchConditionReceptionNo.value !== null &&
        searchConditionReceptionNo.value !== ""
          ? searchConditionReceptionNo.value
          : undefined,
      delivery_type:
        deliveryTypeValues.value !== undefined &&
        deliveryTypeValues.value !== null &&
        deliveryTypeValues.value.value !== 0
          ? deliveryTypeValues.value.value
          : undefined,
      reception_date_from:
        searchConditionReceptionDateFrom.value !== undefined &&
        searchConditionReceptionDateFrom.value !== null
          ? formatDateForDisplay(searchConditionReceptionDateFrom.value)
          : undefined,
      reception_date_to:
        searchConditionReceptionDateTo.value !== undefined &&
        searchConditionReceptionDateTo.value !== null
          ? formatDateForDisplay(searchConditionReceptionDateTo.value)
          : undefined,
      delivery_date_from:
        searchConditionDeliveryDateFrom.value !== undefined &&
        searchConditionDeliveryDateFrom.value !== null
          ? formatDateForDisplay(searchConditionDeliveryDateFrom.value)
          : undefined,
      delivery_date_to:
        searchConditionDeliveryDateTo.value !== undefined &&
        searchConditionDeliveryDateTo.value !== null
          ? formatDateForDisplay(searchConditionDeliveryDateTo.value)
          : undefined,
      area_code:
        areaSelectValues.value !== undefined &&
        areaSelectValues.value !== null &&
        areaSelectValues.value.area_code !== ""
          ? areaSelectValues.value.area_code
          : undefined,
      delivery_status:
        statusSelectedItem.value !== undefined &&
        statusSelectedItem.value !== null &&
        statusSelectedItem.value.length > 0
          ? statusSelectedItem.value
          : undefined,
      chain_code: localStorageGetChainCode(),
      chain_code_list:
        authType.value === "0" || authType.value === "1"
          ? [localStorageGetChainCode()]
          : chainViewFlag.value
          ? searchConditionChainCode.value
          : [localStorageGetChainCode()],
      customer_code: localStorageGetCustomerCode(),
      shop_code_list: searchShopSelectValuesFrom.value,
      delivery_shop_code_list: searchShopSelectValuesTo.value,
      group: localStorageGetGroup(),
      limit: listOptions.value.itemsPerPage,
      offset: (listOptions.value.page - 1) * listOptions.value.itemsPerPage,
    };

    // 配達依頼作成 logInput
    const operationInput: OperationInput = {
      area_code: "10",
      detail: "操作詳細",
      display_category: "画面名",
      operation: "操作",
      user_id: localStorageGetUserId(),
    };

    // 在庫移動・回収依頼件数 input
    const countInput: TransPickupRequestCountInput = {
      reception_no: listInput.reception_no,
      delivery_type: listInput.delivery_type,
      name: listInput.name,
      phone: listInput.phone,
      zip_code: listInput.zip_code,
      reception_date_from: listInput.reception_date_from,
      reception_date_to: listInput.reception_date_to,
      delivery_date_from: listInput.delivery_date_from,
      delivery_date_to: listInput.delivery_date_to,
      area_code: listInput.area_code,
      delivery_status: listInput.delivery_status,
      chain_code: listInput.chain_code,
      chain_code_list: listInput.chain_code_list,
      customer_code: listInput.customer_code,
      shop_code_list: listInput.shop_code_list,
      delivery_shop_code_list: listInput.delivery_shop_code_list,
    };

    // 在庫移動・回収依頼一覧取得
    // 在庫移動・回収依頼件数
    let result: ApolloQueryResult<ListTransPickupRequestQuery>;
    try {
      result = await apolloClient.query<ListTransPickupRequestQuery, ListTransPickupRequestQueryVariables>({
        query: ListTransPickupRequestDocument,
        variables: { listInput, logInput: operationInput, countInput },
        fetchPolicy: "no-cache",
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "不明なエラーが発生しました。";
      }
      errdialog.value = true;
      return;
    }

    if (
      result.data.listTransPickupRequest == null ||
      result.data.getTransPickupRequestCount == null
    ) {
      console.error("在庫移動・回収依頼データ取得エラー");
      return;
    }

    // 配達依頼一覧のデータを作成
    listDataArray.value = [];
    let deliveryId = "";
    let startTime = [];
    let endTime = [];
    const list = result.data.listTransPickupRequest;
    list.forEach((value) => {
      if (
        value !== undefined &&
        value !== null &&
        value.delivery_start_time !== null &&
        value.delivery_start_time !== undefined
      ) {
        startTime = value.delivery_start_time.split(":");
        endTime = value.delivery_end_time!.split(":");
        if (value.delivery_id === 0) {
          deliveryId = "都度";
        } else {
          deliveryId = value.delivery_id.toString();
        }
        deliveryId +=
          "便(" +
          startTime[0].padStart(2, "0") +
          ":" +
          startTime[1].padStart(2, "0") +
          "〜" +
          endTime[0].padStart(2, "0") +
          ":" +
          endTime[1].padStart(2, "0") +
          ")";

        listDataArray.value.push({
          receptionNo: value.reception_no,
          shipFromName: value.ship_from_name,
          deliveryName: value.delivery_name,
          deliveryIdName: deliveryId,
          createdDate: value.created_date,
          plannedDeliveryDate: value.planned_delivery_date
            ? value.planned_delivery_date.substring(0, 10)
            : value.required_delivery_date
            ? value.required_delivery_date.substring(0, 10)
            : "",
          deliveryEstimatedTime: value.planned_delivery_date
            ? value.planned_delivery_date.substring(11, 16)
            : "",
          status: pickupDeliveryStatusName(value.delivery_status),
          deliveryCompletedTime: value.completed_delivery_time!,
          staffName: value.staff_name!,
          deliveryType: pickupDeliveryTypeName(value.delivery_type),
          paymentMethod: value.payment_method ? value.payment_method : "",
          paymentAmount: value.payment_amount
            ? String(value.payment_amount).replace(
                /(\d)(?=(\d\d\d)+(?!\d))/g,
                "$1,"
              )
            : "",
          editAllowed:
            value.edit_allowed === undefined ||
            value.edit_allowed === null ||
            chainViewFlag.value === true
              ? 0
              : value.edit_allowed,
          cancelAllowed: !(
            value.cancel_allowed === undefined ||
            value.cancel_allowed === null ||
            chainViewFlag.value === true ||
            value.cancel_allowed === 0
          ),
        });

        // 削除チェックデータ更新
        if (selectListCheckbox.value.length > 0) {
          selectListCheckbox.value.forEach((CheckboxValue) => {
            if (CheckboxValue.receptionNo === value.reception_no) {
              CheckboxValue.deliveryIdName = deliveryId;
            }
          });
        }
      }
    });

    // 絞込み後の全データ数 更新
    listDataTotalCount.value = result.data.getTransPickupRequestCount.count;

    // ページングの表示
    displayPagingRange.value = displayPagingRangeFunction(
      listOptions.value.page - 1,
      listOptions.value.itemsPerPage,
      listDataTotalCount
    );

    // 編集対象 配達依頼
    editDeliveryRequest.value = null;

    //スマホのモーダルを閉じる
    displayListSearchDialog.value = false;
  };

  // listOptions変化時の検索
  watch(
    () => listOptions.value,
    async () => {
      // 検索ボタンからの呼び出し時はスキップ（onClickSearchで直接処理）
      if (isSearchButtonClick.value) {
        isSearchButtonClick.value = false;
        return;
      }
      listLoading.value = true;
      await onClickSearch(true);
      listLoading.value = false;
    },
    { deep: true }
  );

  /**
   * onMounted処理
   */
  const initOnMounted = async (): Promise<void> => {
    // 在庫移動・回収依頼一覧 ヘッダー を初期化
    initTableHeaders();

    // graphql接続
    const apolloClient = await generateApolloClient();

    // 権限
    authType.value = localStorageGetAuthType();

    try {
      // 文言データを取得する
      Object.assign(wordList, await getWordList(
        localStorageGetChainCode(),
        "ListInventoryMovement",
        localStorageGetCustomerCode()
      ));

      searchShopSelectValuesFrom.value = [];
      searchShopSelectValuesTo.value = [];
      shopListItems.value = [];

      // 運用管理者以外の場合、チェーンコードはログインユーザのチェーンコードにする
      if (chainViewFlag.value) {
        bufChainListItems.value = await getChainList(localStorageGetCustomerCode());
        searchConditionChainCode.value = [];
        shopListInput.chain_code = "";

        if (bufChainListItems.value?.length) {
          chainListItems.value = [];
          bufChainListItems.value.forEach((chain_value: any) => {
            chainListItems.value!.push({ chain_code: chain_value!.chain_code, name: chain_value!.name });
            chainSelectValues.value.push({ chain_code: chain_value!.chain_code, name: chain_value!.name });
            searchConditionChainCode.value.push(chain_value!.chain_code);
          });
        }

        // 店舗情報を取得する
        if (chainSelectValues.value.length === 1) {
          shopListInput.chain_code = chainSelectValues.value[0].chain_code;
          bufShopListItems.value = await getShopList(shopListInput);

          if (bufShopListItems.value.length) {
            bufShopListItems.value.forEach((shop_value: any) => {
              shopListItems.value!.push({ shop_code: shop_value!.shop_code, name: shop_value!.name });
              shopSelectValuesFrom.value.push({ shop_code: shop_value!.shop_code, name: shop_value!.name });
              searchShopSelectValuesFrom.value.push(shop_value!.shop_code);
              shopSelectValuesTo.value.push({ shop_code: shop_value!.shop_code, name: shop_value!.name });
              searchShopSelectValuesTo.value.push(shop_value!.shop_code);
            });
          }

          shopDisabledFlag.value = false;
          shopFromAllCheckFlag.value = "all";
          shopToAllCheckFlag.value = "all";
        } else {
          shopFromAllCheckFlag.value = "none";
          shopToAllCheckFlag.value = "none";
          shopDisabledFlag.value = true;
        }
      } else {
        // 店舗情報を取得する
        bufShopListItems.value = await getShopList(shopListInput);

        // 店舗権限の場合は自身の店舗を配送元のデフォルトにする
        if (bufShopListItems.value.length > 0) {
          bufShopListItems.value.forEach((shop_value: any) => {
            shopListItems.value!.push({ shop_code: shop_value!.shop_code, name: shop_value!.name });

            if (isOperatorGroupShop.value) {
              if (shop_value!.shop_code === localStorageGetShopCode()) {
                shopSelectValuesFrom.value.push({ shop_code: shop_value!.shop_code, name: shop_value!.name });
                searchShopSelectValuesFrom.value.push(shop_value!.shop_code);
              }
              if (bufShopListItems.value.length === 1) {
                shopFromAllCheckFlag.value = "all";
              } else if (bufShopListItems.value.length > 1) {
                shopFromAllCheckFlag.value = "some";
              }
            } else {
              shopSelectValuesFrom.value.push({ shop_code: shop_value!.shop_code, name: shop_value!.name });
              searchShopSelectValuesFrom.value.push(shop_value!.shop_code);
              shopFromAllCheckFlag.value = "all";
            }

            searchShopSelectValuesTo.value.push(shop_value!.shop_code);
            shopSelectValuesTo.value.push({ shop_code: shop_value!.shop_code, name: shop_value!.name });
          });
        }
        shopToAllCheckFlag.value = "all";
        shopDisabledFlag.value = false;
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err);
        errMessage.value = err.message;
      } else {
        console.error("未知のエラー:", err);
        errMessage.value = "不明なエラーが発生しました。";
      }
      errdialog.value = true;
      return;
    }

    // チェーンごと支払方法取得
    paymentList.value = await getChainPaymentMethod(
      localStorageGetCustomerCode(),
      localStorageGetChainCode()
    );
    paymentMethodItems.value = paymentList.value!.map((p: any) => p?.payment_method);

    /**
     * 個口数
     */
    const max_unit = await apolloClient.query<GetChainQuery>({
      query: GetChainDocument,
      variables: {
        chain_code: localStorageGetChainCode(),
        customer_code: localStorageGetCustomerCode(),
      },
    });

    unitItems.value = [];
    for (let i = 0; i < Number(max_unit.data.getChain!.max_unit); i++) {
      unitItems.value.push(i + 1);
    }

    isMounted.value = true;
  };

  return {
    isSearchButtonClick,
    onClickSearch,
    initOnMounted,
  };
};
